<template>
  <div class="partner-sso">
    <div class="partner-sso-card">
      <b-spinner
        v-if="!hasError"
        class="partner-sso-spinner"
      />

      <template v-if="!hasError">
        <h4 class="partner-sso-title">
          Conectando con el portal de pagos
        </h4>
        <p class="partner-sso-text">
          Un momento, estamos preparando su pago.
        </p>
      </template>

      <template v-else>
        <feather-icon
          icon="AlertCircleIcon"
          size="42"
          class="text-danger mb-1"
        />
        <h4 class="partner-sso-title">
          No pudimos abrir el portal
        </h4>
        <p class="partner-sso-text">
          {{ errorMessage }}
        </p>
        <p class="partner-sso-hint">
          Vuelva a la aplicación e intente nuevamente.
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import { BSpinner } from "bootstrap-vue"
// Ciclo preexistente: auth/jwt/useJwt -> @axios -> @/router (mismo caso que Contract.vue)
// eslint-disable-next-line import/no-cycle
import useJwt from "@/auth/jwt/useJwt"

/**
 * Entrada al portal desde la app de un integrador externo.
 *
 * Diferencia con VerifyMagicLink: acá NO hay pantalla de confirmación. Ese tap
 * existe en el flujo por correo para protegerse del prefetch de los clientes de
 * email y de que alguien abra un enlace ajeno. Nada de eso aplica: este enlace
 * lo pidió la app del integrador segundos atrás, lo emitió nuestro backend, y
 * dura 5 minutos. Pedir confirmación acá sería fricción sin beneficio.
 *
 * El token se canjea una sola vez y la sesión que devuelve solo sirve para
 * pagar el contrato que el propio token nombra.
 */
export default {
  components: { BSpinner },

  data() {
    return {
      hasError: false,
      errorMessage: "",
    }
  },

  computed: {
    /**
         * El token de Sanctum tiene forma `id|hash`, y la barra vertical no es
         * un carácter válido sin escapar en una URL: el navegador la manda como
         * %7C. vue-router normalmente ya lo decodifica, pero algunos WebViews y
         * proxies vuelven a codificarlo. Decodificar de nuevo es inofensivo
         * — estos tokens son alfanuméricos más el pipe, sin ningún % — y evita
         * un fallo que solo aparecería en ciertos dispositivos.
         */
    ssoToken() {
      const raw = this.$route.params.token || ""

      try {
        return decodeURIComponent(raw)
      } catch (e) {
        return raw
      }
    },
  },

  mounted() {
    this.redeem()
  },

  methods: {
    async redeem() {
      try {
        // Nunca mezclar identidades: si el navegador traía una sesión
        // previa, se descarta antes de escribir la nueva.
        this.clearSession()

        const { data } = await useJwt.redeemPartnerSso(this.ssoToken)

        if (!data || !data.accessToken || !data.contractId) {
          throw new Error("Respuesta incompleta del servidor")
        }

        useJwt.setToken(data.accessToken)
        useJwt.setRefreshToken(data.refreshToken || data.accessToken)
        localStorage.setItem("userData", JSON.stringify(data.userData))

        if (data.userData && data.userData.ability) {
          this.$ability.update(data.userData.ability)
        }

        /*
                 * El destino lo decide el backend, no esta vista: viene en
                 * redirectTo, derivado del contrato que el propio token nombra.
                 * Se valida que sea una ruta interna antes de usarlo — nunca
                 * una URL absoluta — para que no exista un open redirect ni
                 * aunque el backend cambie.
                 */
        const target =
                    typeof data.redirectTo === "string" &&
                    data.redirectTo.startsWith("/") &&
                    !data.redirectTo.startsWith("//")
                      ? data.redirectTo
                      : `/pagar/${data.contractId}`

        this.$router.replace(target)
      } catch (error) {
        this.clearSession()
        this.hasError = true
        this.errorMessage =
                    error.response?.data?.message ||
                    "El enlace ya fue utilizado o expiró."
      }
    },

    clearSession() {
      localStorage.removeItem("accessToken")
      localStorage.removeItem("refreshToken")
      localStorage.removeItem("userData")
    },
  },
}
</script>

<style lang="scss" scoped>
.partner-sso {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 1.5rem;
}

.partner-sso-card {
    max-width: 22rem;
    text-align: center;
}

.partner-sso-spinner {
    width: 2.5rem;
    height: 2.5rem;
    margin-bottom: 1.25rem;
}

.partner-sso-title {
    margin-bottom: 0.5rem;
}

.partner-sso-text {
    margin-bottom: 0;
    color: #6e6b7b;
}

.partner-sso-hint {
    margin-top: 0.75rem;
    margin-bottom: 0;
    font-size: 0.875rem;
    color: #b9b9c3;
}
</style>
